import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Send, MessagesSquare, Trash2 } from "lucide-react";

function initials(name) {
  return (name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}
function timeLabel(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export default function Chat() {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
      else setLoading(false);
    });
    api.post("/comms/seen/chat").catch(() => {});
  }, []);

  const load = useCallback((pid, scroll = false) => {
    if (!pid) return;
    api.get("/chat/messages", { params: { property_id: pid } })
      .then((r) => {
        setMessages(r.data);
        if (scroll) setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 50);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    load(propertyId, true);
    api.post("/comms/seen/chat").catch(() => {});
    const t = setInterval(() => load(propertyId, false), 5000);
    return () => clearInterval(t);
  }, [propertyId, load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || !propertyId) return;
    setSending(true);
    try {
      await api.post("/chat/messages", { property_id: propertyId, text: t });
      setText("");
      load(propertyId, true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/chat/messages/${id}`);
    load(propertyId, false);
  };

  const propName = props.find((p) => p.id === propertyId)?.name || "";

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col space-y-4" data-testid="chat-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Communications</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Team Chat</h1>
          <p className="mt-1 text-sm text-muted-foreground">Quick questions & answers for <span className="font-semibold text-foreground">{propName || "your property"}</span>. Each property has its own chat, kept as history.</p>
        </div>
        <div className="w-64">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger data-testid="chat-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
            <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col border border-border bg-card">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-auto p-4" data-testid="chat-messages">
          {loading ? (
            <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
              <MessagesSquare className="h-8 w-8" /><p className="mt-3 text-sm">No messages yet for {propName}. Start the conversation.</p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.user_id === user?.id;
              return (
                <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{initials(m.user_name)}</div>
                  <div className={`max-w-[70%] ${mine ? "items-end text-right" : ""}`}>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{m.user_name}</span>
                      <span>· {ROLE_LABELS[m.role] || m.role}</span>
                      <span>· {timeLabel(m.created_at)}</span>
                    </div>
                    <div className={`mt-1 inline-block whitespace-pre-wrap break-words px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                      {m.text}
                    </div>
                    {user?.role === "admin" && (
                      <button onClick={() => remove(m.id)} className="ml-2 text-xs text-muted-foreground hover:text-destructive" data-testid={`chat-delete-${m.id}`}>
                        <Trash2 className="inline h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="flex items-center gap-2 border-t border-border p-3">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Message ${propName}…`}
            disabled={!propertyId}
            data-testid="chat-input"
          />
          <Button onClick={send} disabled={sending || !text.trim() || !propertyId} data-testid="chat-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
